import { NgClass, NgIf } from '@angular/common';
import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { toObservable } from "@angular/core/rxjs-interop";
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from "@angular/material/input";
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer, MatSidenavModule } from "@angular/material/sidenav";
import { RouterLink, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseLoadingService } from '@fuse/services/loading';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { CompanyTreeService } from "app/core/company/company-tree.service";
import { CompanyService } from 'app/core/company/company.service';
import { Company } from 'app/core/company/company.types';
import { useCompanyAccessStatus } from 'app/core/helpers/company-access.helper';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { PushNotificationService } from 'app/core/push/push-notification.service';
import { LowerCamelCasePipe } from 'app/core/transloco/pipes/lower-camel-case.pipe';
import { CurrentUserService } from 'app/core/user/current-user.service';
import { User } from 'app/core/user/user.types';
import { CompanyTreeSidePanelComponent } from "app/layout/common/company-tree-side-panel/company-tree-side-panel.component";
import { CompanyTreeComponent } from "app/layout/common/company-tree/company-tree.component";
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { AlertDialogComponent } from 'app/modules/admin/common/alert-dialog/alert-dialog.component';
import { Subject, takeUntil } from 'rxjs';
import { WorkingCompany } from '../../../../core/company/working-company';
import { AlertDisplayComponent } from '../../../common/alert/alert-dispay.component';
import { DoorwifiAdvertisement } from "../../../common/doorwifi-advertisement/doorwifi-advertisement.component";

@Component({
    selector     : 'classic-doorwifi-layout',
    templateUrl  : './classic-doorwifi.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    NotificationsComponent,
    UserComponent,
    NgIf,
    RouterOutlet,
    MatSidenavModule,
    MatInputModule,
    CompanyTreeComponent,
    TranslocoPipe,
    AlertDisplayComponent,
    NgClass,
    MatButton,
    MatMenu,
    DoorwifiAdvertisement,
    MatMenuTrigger,
    LowerCamelCasePipe,
    RouterLink,
    CompanyTreeSidePanelComponent
],
})
export class ClassicDoorwifiLayoutComponent implements OnInit, OnDestroy
{
    pageIsLoading: boolean = false;
    isScreenSmall: boolean = false;
    drawerOpened: boolean = false;
    shouldDisplayTree: boolean = false;

    navigation: Navigation;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    readonly companyStatus = useCompanyAccessStatus();

    private _pushInitialized = false;

    @ViewChild('drawer') drawer: MatDrawer;
    navigationAppearance: 'default' | 'dense' = 'default';
    companyData: Company = null;

    companyPanelOpen = false;
    companyPanelMode: 'view' | 'edit' = 'view';
    selectedCompanyData: Company | null = null;

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseLoadingService: FuseLoadingService,
        private _userService: CurrentUserService,
        private _companyTreeService: CompanyTreeService,
        private _translocoService: TranslocoService,
        private _companyService: CompanyService,
        protected workingCompany: WorkingCompany,
        private _pushService: PushNotificationService,
        private _matDialog: MatDialog
    )
    {
        // Subscribe to tree should display boolean change
        toObservable(this._companyTreeService.shouldDisplayTree).subscribe(result => {
            this.shouldDisplayTree = result;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
            this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) => {
                if (!user) return;

                if (!this._pushInitialized) {
                    this._pushInitialized = true;
                    this._pushService.init().catch(() => {});
                }

                //get current company data
                if (user.companyId) {
                this._companyService.get(user.companyId).subscribe(company => {
                    this.companyData = company;
                    });
                }

                if (user.language) {
                    this._translocoService.setActiveLang(user.language);
                }
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                if (this.isScreenSmall) {
                    this.navigationAppearance = 'default';
                }
            });

        // Subscribe to fuse loading service.
        this._fuseLoadingService.show$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                setTimeout(() => {
                    this.pageIsLoading = result;
                });
            });
    }

    /**
     * Open delete company confirmation dialog
     */
    private openDeleteCompanyDialog(): void {
        const selectedCompany = this._companyTreeService.selectedCompany();

        if (!selectedCompany?.id) {
            return;
        }

        const dialogRef = this._matDialog.open(AlertDialogComponent, {
            data: {
                title: 'Delete company',
                messages: [
                    selectedCompany.name,
                    'This action will delete the company and all its dependencies.',
                    'Note: You cannot delete a company that has assigned devices, eSAT licenses, or is a (sub)distributor with companies under its service. You must remove those entities first.'
                ],
                color: 'warn',
                icon: 'heroicons_outline:trash',
                actions: {
                    showConfirm: true,
                    showCancel: true,
                    confirmLabel: 'Delete company',
                    cancelLabel: 'Cancel'
                }
            },
            disableClose: true
        });

        dialogRef.afterClosed().subscribe((result: 'confirmed' | 'cancelled' | 'dismissed' | undefined) => {
            if (result !== 'confirmed') {
                return;
            }

            this._companyService.delete(selectedCompany.id).subscribe({
                next: () => {
                    this.closeCompanyPanel();

                    if (this.drawer?.opened) {
                        this.drawer.close();
                    }

                    this._companyTreeService.get().subscribe();

                    this._matDialog.open(AlertDialogComponent, {
                        data: {
                            title: 'Company deleted',
                            messages: [
                                'The company was successfully deleted.'
                            ],
                            color: 'primary',
                            icon: 'heroicons_solid:check-circle',
                            actions: {
                                showConfirm: true,
                                showCancel: false,
                                confirmLabel: 'Continue'
                            }
                        }
                    });
                },
                error: () => {
                    this._matDialog.open(AlertDialogComponent, {
                        data: {
                            title: 'Delete failed',
                            messages: [
                                'The company could not be deleted. Please remove all dependencies first.'
                            ],
                            color: 'warn',
                            icon: 'heroicons_outline:exclamation-triangle',
                            actions: {
                                showConfirm: true,
                                showCancel: false,
                                confirmLabel: 'Continue'
                            }
                        }
                    });
                }
            });
        });
    }

    /**
     * Generate company link code
     */
    onGenerateLinkCode(): void {
        if (!this.selectedCompanyData?.id) {
            return;
        }

        this._companyService.generateLinkCode(this.selectedCompanyData.id).subscribe({
            next: (res: { code: string }) => {
                if (!this.selectedCompanyData) {
                    return;
                }

                this.selectedCompanyData.linkCode = res?.code || '';
            }
        });
    }

    /**
     * On tree action (view, delete)
     */
    onTreeAction(action: 'view' | 'delete'): void {
        const selectedCompany = this._companyTreeService.selectedCompany();

        if (!selectedCompany) {
            return;
        }

        if (action === 'delete') {
            this.openDeleteCompanyDialog();
            return;
        }

        this.companyPanelMode = 'view';
        this.companyPanelOpen = true;

        if (!selectedCompany.id) {
            return;
        }

        this._companyService.get(selectedCompany.id).subscribe(company => {
            this.selectedCompanyData = company;
        });

        if (this.drawer && !this.drawer.opened) {
            this.drawer.open();
        }
    }

    /**
     * Close company panel
     */
    closeCompanyPanel(): void {
        this.companyPanelOpen = false;
        this.companyPanelMode = 'view';
        this.selectedCompanyData = null;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
    }

    /**
     * Toggle company tree
     */
    toggleCompanyTree(): void {
        if (this.drawer) {
            this.drawer.toggle();
        }
    }
}
